import "./ripple.scss";
import type { Directive, DirectiveBinding } from "vue";

// ENG-20861: in-house replacement for quasar's Ripple directive. Material-style ripple on
// pointer-down (and keyboard activation), animated purely in the DOM. `v-ripple="false"`
// disables it. Mirrors quasar's timing (50ms grow → 250ms hold → 275ms fade-out).

interface RippleEl extends HTMLElement {
  __mRipple?: {
    enabled: boolean;
    onPointerDown: (evt: PointerEvent) => void;
    onKeyDown: (evt: KeyboardEvent) => void;
  };
}

function showRipple(el: HTMLElement, x: number, y: number) {
  // the ripple span is absolutely positioned within the host; ensure the host is a
  // positioning context so inset:0 maps to its box (quasar required the same).
  const pos = getComputedStyle(el).position;
  if (pos === "static" || pos === "") {
    el.style.position = "relative";
  }

  const node = document.createElement("span");
  const inner = document.createElement("span");
  const { width, height } = el.getBoundingClientRect();
  const diameter = Math.sqrt(width * width + height * height);
  const radius = diameter / 2;
  const centerX = `${(width - diameter) / 2}px`;
  const centerY = `${(height - diameter) / 2}px`;
  const startX = `${x - radius}px`;
  const startY = `${y - radius}px`;

  inner.className = "m-ripple__inner";
  inner.style.height = `${diameter}px`;
  inner.style.width = `${diameter}px`;
  inner.style.transform = `translate3d(${startX},${startY},0) scale3d(.2,.2,1)`;
  inner.style.opacity = "0";

  node.className = "m-ripple";
  node.setAttribute("dir", "ltr");
  node.appendChild(inner);
  el.appendChild(node);

  setTimeout(() => {
    inner.classList.add("m-ripple__inner--enter");
    inner.style.transform = `translate3d(${centerX},${centerY},0) scale3d(1,1,1)`;
    inner.style.opacity = "0.2";

    setTimeout(() => {
      inner.classList.remove("m-ripple__inner--enter");
      inner.classList.add("m-ripple__inner--leave");
      inner.style.opacity = "0";

      setTimeout(() => {
        node.remove();
      }, 275);
    }, 250);
  }, 50);
}

function isEnabled(binding: DirectiveBinding): boolean {
  return binding.value !== false;
}

export const ripple: Directive<RippleEl> = {
  mounted(el, binding) {
    const ctx = {
      enabled: isEnabled(binding),
      onPointerDown(evt: PointerEvent) {
        if (!ctx.enabled) return;
        const { left, top } = el.getBoundingClientRect();
        showRipple(el, evt.clientX - left, evt.clientY - top);
      },
      onKeyDown(evt: KeyboardEvent) {
        if (!ctx.enabled) return;
        if (evt.key !== "Enter" && evt.key !== " ") return;
        const { width, height } = el.getBoundingClientRect();
        showRipple(el, width / 2, height / 2);
      },
    };
    el.__mRipple = ctx;
    el.addEventListener("pointerdown", ctx.onPointerDown);
    el.addEventListener("keydown", ctx.onKeyDown);
  },

  updated(el, binding) {
    if (el.__mRipple) el.__mRipple.enabled = isEnabled(binding);
  },

  beforeUnmount(el) {
    if (el.__mRipple) {
      el.removeEventListener("pointerdown", el.__mRipple.onPointerDown);
      el.removeEventListener("keydown", el.__mRipple.onKeyDown);
      delete el.__mRipple;
    }
  },
};

export default ripple;
